import { useContext } from "react";
import { Image, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useQuery } from "react-query";
import { Link } from "react-router-dom";

import {
	faAngleDoubleLeft,
	faPaperclip,
	faRefresh,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { getOrgAvatar } from "../api";
import { SiteContext } from "../contexts/siteContext";
import { UserDataContext } from "../contexts/userDataContext";
import { lifetimeDataQuerySetting } from "./customQueryClientProvider";

export const Banner = ({ children }: { children: any }) => (
	<div className="banner ps-3" data-testid="test-banner">
		{children}
	</div>
);

const BannerBackButton = ({ path }: { path: string }) => (
	<Link className="d-block" to={path}>
		<h4 className="mb-0 text-charcoal opacity-50">
			<FontAwesomeIcon icon={faAngleDoubleLeft} />
		</h4>
	</Link>
);

const BannerRefreshButton = ({ refreshFn }: { refreshFn: () => void }) => (
	<a className="me-4" onClick={refreshFn}>
		<h4 className="mb-0">
			<FontAwesomeIcon icon={faRefresh} />
		</h4>
	</a>
);

const BannerLinkButton = () => (
	<OverlayTrigger
		overlay={<Tooltip id="button-tooltip">Save link to clipboard</Tooltip>}
	>
		<a
			className="me-4"
			onClick={() => navigator.clipboard.writeText(window.location.href)}
		>
			<h4 className="mb-0">
				<FontAwesomeIcon icon={faPaperclip} />
			</h4>
		</a>
	</OverlayTrigger>
);

interface DataDetailBannerProps {
	title?: string;
	description?: string;
	path?: string;
	refreshFn?: () => void;
	children?: any;
	isPadded?: boolean;
}

export const DataDetailBanner = ({
	title = "",
	description,
	path,
	refreshFn,
	children,
	isPadded = true,
}: DataDetailBannerProps) => (
	<Banner>
		<div
			className={`d-flex company-banner justify-content-start flex-wrap flex-md-nowrap align-items-center ${
				isPadded ? "py-3" : "py-2"
			}`}
		>
			{children}
			{path ? <BannerBackButton path={path} /> : null}
			<div className="ms-3 py-2">
				<h4 className="mb-0">{title}</h4>
				{description ? <span>{description}</span> : null}
			</div>
			<div className="ms-auto d-flex flex-nowrap">
				<BannerLinkButton />
				{refreshFn ? (
					<BannerRefreshButton refreshFn={refreshFn} />
				) : null}
			</div>
		</div>
	</Banner>
);
export const OrgAvatarBanner = () => {
	const { selectedOrg } = useContext(UserDataContext);

	const { data: orgProfilePic } = useQuery(
		[Object.keys({ getOrgAvatar })[0], selectedOrg?.id],
		() => getOrgAvatar({ orgId: selectedOrg?.id }),
		{
			...lifetimeDataQuerySetting,
			enabled: !!selectedOrg?.id,
		}
	);

	return (
		<DataDetailBanner
			isPadded={false}
			title={selectedOrg?.name}
			description={selectedOrg?.description}
		>
			<Image
				src={orgProfilePic?.avatar_Base64}
				className="company-logo ps-2 py-2 me-2 my-auto d-none d-lg-block"
			/>
		</DataDetailBanner>
	);
};

export const SiteAvatarBanner = () => {
	const { selectedOrg } = useContext(UserDataContext);
	const { selectedSite } = useContext(SiteContext);

	const { data: orgProfilePic } = useQuery(
		[Object.keys({ getOrgAvatar })[0], selectedOrg?.id],
		() => getOrgAvatar({ orgId: selectedOrg?.id }),
		{
			...lifetimeDataQuerySetting,
			enabled: !!selectedOrg?.id,
		}
	);

	return (
		<DataDetailBanner
			isPadded={false}
			title={selectedSite?.name}
			description={selectedOrg?.name}
		>
			<Image
				src={orgProfilePic?.avatar_Base64}
				className="company-logo ps-2 py-2 me-2 my-auto d-none d-lg-block"
			/>
		</DataDetailBanner>
	);
};
